'use client'

import * as React from 'react'

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { Loading } from '@/components/customComponents'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '../button'
import { DataTablePagination } from './DataTablePagination'
import { DataTableViewOptions } from './DataTableViewOptions'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  height?: number
  selectedBranch?: string
  pagination?: boolean
  tableViewOption?: boolean
  cardTitle?: string
  cardDescription?: string
  loading?: boolean
  isCard?: boolean
  onClose?: () => void
  children?: JSX.Element | JSX.Element[]
  footer?: JSX.Element | JSX.Element[]
}

export function DataTable<TData, TValue>({
  columns,
  data,
  height = 35,
  selectedBranch,
  pagination = false,
  tableViewOption = false,
  cardTitle,
  cardDescription,
  loading = false,
  isCard = true,
  onClose,
  children,
  footer,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const filteredData = React.useMemo(() => {
    if (selectedBranch === 'All Locations') {
      return data
    }

    if (selectedBranch) {
      data = [
        ...data.filter(
          (item) => (item as { stand: string }).stand === 'Company System'
        ),
        ...data.filter(
          (item) => (item as { stand: string }).stand === selectedBranch
        ),
      ]

      return data
    }
    return data
  }, [data, selectedBranch])

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
  })

  function renderCard(children: JSX.Element | JSX.Element[]) {
    if (isCard) {
      return (
        <Card>
          {(cardTitle || cardDescription) && (
            <>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{cardTitle}</CardTitle>
                  {onClose && (
                    <div className="flex justify-end">
                      <Button onClick={onClose}>Back</Button>
                    </div>
                  )}
                </div>
                <CardDescription>{cardDescription}</CardDescription>
              </CardHeader>
            </>
          )}
          <CardContent className="p-0">{children}</CardContent>
        </Card>
      )
    }

    return <>{children}</>
  }

  return (
    <>
      {renderCard(
        <>
          {loading ? (
            <div className="flex items-center justify-center h-72">
              <Loading />
            </div>
          ) : (
            <div
              className={`overflow-x-auto h-[${height}vh] max-h-[${height}vh] overflow-y-auto`}
            >
              {tableViewOption && (
                <div className="mb-2 flex space-x-2">
                  <DataTableViewOptions table={table} />
                </div>
              )}
              {children && (
                <div className="mb-2 flex space-x-2">{children}</div>
              )}
              <div className="rounded-xl border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => {
                          return (
                            <TableHead
                              key={header.id}
                              colSpan={header.colSpan}
                              // className={
                              //   index === 0
                              //     ? 'sticky left-0 z-10 bg-white border-r'
                              //     : header.colSpan > 1
                              //       ? 'border-r border-l text-center'
                              //       : 'bg-white'
                              // }
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row, rowIndex) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          // className={`${(row.original as { stand: string }).stand ===
                          //   'Company System'
                          //   ? 'border-2 border-accent'
                          //   : rowIndex % 2 === 0
                          //     ? 'bg-gray-100'
                          //     : 'bg-white/50'
                          //   }`}
                        >
                          {row.getVisibleCells().map((cell, index) => (
                            <TableCell
                              key={cell.id}
                              className="max-w-0"
                              // className={
                              //   index === 0
                              //     ? 'sticky left-0 z-10 bg-white border-r'
                              //     : ''
                              // }
                            >
                              <div className="line-clamp-2 overflow-hidden">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          -
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {pagination && <DataTablePagination table={table} />}
              {footer && <div className="mt-6 flex justify-end">{footer}</div>}
            </div>
          )}
        </>
      )}
    </>
  )
}
